import { useEffect, useRef, useState } from "react";

import { AlertTriangle, Bot, Brain, Eye, Globe, Search, Shield, Zap } from "lucide-react";

/* ── Witty cycling messages ──────────────────────────────────────────── */
const WITTY_MESSAGES = [
  { text: "Teaching our AI to read between the <meta> tags…", icon: Search },
  { text: "Convincing schema markup to spill its secrets…", icon: Search },
  { text: "Asking Google's crawlers for a second opinion…", icon: Globe },
  {
    text: "Running your site through our crystal ball algorithm…",
    icon: Brain,
  },
  { text: "Counting every pixel of structured data…", icon: Eye },
  { text: "Whispering sweet nothings to your robots.txt…", icon: Bot },
  { text: "Performing open-heart surgery on your HTML…", icon: Zap },
  { text: "Making your website confess its SEO sins…", icon: Shield },
  { text: "Translating your content into AI-speak…", icon: Brain },
  {
    text: "Checking if ChatGPT would swipe right on your hotel…",
    icon: Search,
  },
  {
    text: "Auditing your site harder than the IRS audits expenses…",
    icon: Search,
  },
  { text: "Evaluating if your hotel passes the vibe check…", icon: Eye },
  { text: "Putting your website on the therapist's couch…", icon: Bot },
  { text: "Decoding the matrix of your online presence…", icon: Globe },
  { text: "Running 47 different AI models in a trench coat…", icon: Zap },
];

/* ── Helpers ──────────────────────────────────────────────────────────── */
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/* ── Component ───────────────────────────────────────────────────────── */
const AuditLoaderDialog = ({ url }) => {
  const [elapsed, setElapsed] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);

  // Elapsed timer
  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Cycle witty messages every 4s
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % WITTY_MESSAGES.length);
        setIsTransitioning(false);
      }, 400);
    }, 10000);
    return () => clearInterval(interval);
  }, [msgIndex]);

  const showLargeSiteWarning = elapsed >= 60;
  const showBotBlockWarning = elapsed >= 180;

  const currentMsg = WITTY_MESSAGES[msgIndex];
  const CurrentIcon = currentMsg.icon;

  return (
    <div className="audit-loader-overlay">
      <div className="audit-loader-card">
        {/* ── Spinner (rings only, no orbiting dots) ───────────── */}
        <div className="audit-spinner-container">
          <div className="audit-spinner-ring audit-ring-1" />
          <div className="audit-spinner-ring audit-ring-2" />
          <div className="audit-spinner-ring audit-ring-3" />
          <div className="audit-spinner-core">
            <Bot className="w-8 h-8 text-primary" />
          </div>
        </div>

        {/* ── Default Status ───────────────────────────────────── */}
        <div className="audit-phase-badge">
          <span className="audit-phase-dot" />
          Crawling & Analyzing
        </div>

        <h2 className="audit-loader-title">Fetching All URLs & Analyzing with AI</h2>
        <p className="audit-loader-subtitle">
          Our bots are crawling every page on your site and running it through our LLM engine.
        </p>

        {/* ── URL Being Audited ───────────────────────────────── */}
        <div className="audit-url-chip">
          <Globe className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate">{url}</span>
        </div>

        {/* ── Timed Warning: Large Site (>60s) ─────────────────── */}
        {showLargeSiteWarning && (
          <div className="audit-warning audit-warning-amber">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <span>
              Still working! Your website appears to be quite large — hang tight, our bots are
              processing all pages.
            </span>
          </div>
        )}

        {/* ── Timed Warning: Bot Blocked (>180s) ──────────────── */}
        {showBotBlockWarning && (
          <div className="audit-warning audit-warning-purple">
            <Shield className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span>
              Your website seems to block bots 🤖 — no worries, we&apos;ve switched to our…
              let&apos;s call them &lsquo;creative extraction techniques&rsquo;. We shall have your
              site shortly
            </span>
          </div>
        )}

        {/* ── Cycling Witty Message ────────────────────────────── */}
        <div className="audit-witty-container">
          <div
            className={`audit-witty-msg ${isTransitioning ? "audit-witty-exit" : "audit-witty-enter"}`}
          >
            <CurrentIcon className="w-4 h-4 text-primary shrink-0" />
            <span>{currentMsg.text}</span>
          </div>
        </div>

        {/* ── Progress Dots ────────────────────────────────────── */}
        <div className="audit-progress-dots">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="audit-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>

        {/* ── Elapsed Timer ────────────────────────────────────── */}
        <div className="audit-elapsed">
          <span className="audit-elapsed-label">Elapsed</span>
          <span className="audit-elapsed-time">{formatTime(elapsed)}</span>
        </div>

        {/* ── Dynamic ETA ──────────────────────────────────────── */}
        <div className="text-xs text-on-surface-variant mt-2 font-medium">
          Estimated time:{" "}
          <span className="text-primary">
            {elapsed > 180 ? "5-10 min" : elapsed > 60 ? "3-5 min" : "1-2 min"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AuditLoaderDialog;
